import { mergeAttributes, Node } from '@tiptap/core';
import {
  type NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  useEditorState,
} from '@tiptap/react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CalendarDays,
  Check,
  Copy,
  Ellipsis,
  Filter,
  GripVertical,
  Hash,
  Link2,
  Settings2,
  SquareCheck,
  Table2,
  Tag,
  Tags,
  Trash2,
  Type,
  X,
} from 'lucide-react';
import { type PointerEvent, type ReactNode, useEffect, useRef, useState } from 'react';

import {
  type Cell,
  type Column,
  type ColumnType,
  newTable,
  type TypedTable,
} from '../../shared/model';
import {
  appendTableRow,
  cellText,
  changeEmptyColumnType,
  displayCell,
  moveColumn,
  optionsFor,
  parseCell,
  removeColumn,
  tableView,
} from '../../shared/table';

import {
  ActionIconButton,
  Button,
  CellTags,
  ControlPopover,
  Disclosure,
  IconButton,
  Input,
  MultiSelectCell,
  Select,
} from './ui';

const types: { value: ColumnType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'select', label: 'Single-select' },
  { value: 'multi', label: 'Multi-select' },
  { value: 'url', label: 'URL' },
];

const typeIcons = {
  text: Type,
  number: Hash,
  date: CalendarDays,
  checkbox: SquareCheck,
  select: Tag,
  multi: Tags,
  url: Link2,
};

function CellEditor({
  locked,
  column,
  value,
  rowNumber,
  onChange,
  onAdvance,
}: {
  locked: boolean;
  column: Column;
  value: Cell | undefined;
  rowNumber: number;
  onChange: (value: Cell) => void;
  onAdvance?: (value: Cell) => void;
}) {
  const skipBlur = useRef(false);
  const [editing, setEditing] = useState(false);

  const [draft, setDraft] = useState(cellText(value)),
    [error, setError] = useState(''),
    [copied, setCopied] = useState(false),
    [copyError, setCopyError] = useState('');

  useEffect(() => {
    setDraft(cellText(value));
    setError('');
  }, [value]);

  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => setCopied(false), 1800);

      return () => clearTimeout(timer);
    }
  }, [copied]);

  const commit = () => {
    setEditing(false);

    if (locked) {
      return;
    }

    if (skipBlur.current) {
      skipBlur.current = false;

      return;
    }

    try {
      const parsed = parseCell(column, draft);

      onChange(parsed);
      setDraft(cellText(parsed));
      setError('');
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const formattedDraft = () => {
    if (column.type !== 'number') {
      return draft;
    }

    try {
      return displayCell(column, parseCell(column, draft));
    } catch {
      return draft;
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(
        ['select', 'multi', 'checkbox'].includes(column.type)
          ? displayCell(column, value)
          : formattedDraft(),
      );

      setCopied(true);
      setCopyError('');
    } catch {
      setCopyError('Copy failed. Select the value and copy it manually.');
    }
  };

  let field: ReactNode;

  if (column.type === 'checkbox') {
    field = (
      <Input
        disabled={locked}
        type="checkbox"
        aria-label={column.name}
        checked={value === true}
        onChange={e => onChange(e.target.checked)}
      />
    );
  } else if (column.type === 'select') {
    field = (
      <Select
        disabled={locked}
        variant="cell"
        aria-label={column.name}
        value={cellText(value)}
        displayValue={
          <CellTags labels={value == null || value === '' ? [] : [displayCell(column, value)]} />
        }
        onChange={e => onChange(e.target.value || null)}
      >
        <option value="">Clear selection</option>
        {optionsFor(column).map(option => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </Select>
    );
  } else if (column.type === 'multi') {
    field = (
      <MultiSelectCell
        disabled={locked}
        label={column.name}
        options={optionsFor(column)}
        value={Array.isArray(value) ? value : []}
        onChange={onChange}
      />
    );
  } else {
    field = (
      <Input
        disabled={locked}
        variant="cell"
        type={column.type === 'date' ? 'date' : 'text'}
        placeholder=""
        inputMode={column.type === 'number' ? 'decimal' : undefined}
        aria-label={column.name}
        aria-invalid={Boolean(error)}
        value={column.type === 'number' && !editing ? formattedDraft() : draft}
        onFocus={() => setEditing(true)}
        onInput={e => setDraft(e.currentTarget.value)}
        onChange={
          column.type === 'date'
            ? e => {
                try {
                  onChange(parseCell(column, e.currentTarget.value));
                  setError('');
                } catch (error) {
                  setError((error as Error).message);
                }
              }
            : undefined
        }
        onBlur={column.type === 'date' ? undefined : commit}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          }
        }}
      />
    );
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: the wrapper only observes Tab bubbling from its focusable child input to append a row
    <div
      className="table-cell"
      data-type={column.type}
      onKeyDown={event => {
        const target = event.target as HTMLElement;

        if (
          locked ||
          !onAdvance ||
          event.key !== 'Tab' ||
          event.shiftKey ||
          event.altKey ||
          event.ctrlKey ||
          event.metaKey ||
          !event.currentTarget.contains(target) ||
          !target.matches('input,button[role="combobox"],button.ui-cell-choice') ||
          target.getAttribute('aria-expanded') === 'true'
        ) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        if (target instanceof HTMLInputElement && !target.checkValidity()) {
          target.reportValidity();

          return;
        }

        try {
          const immediate = ['checkbox', 'select', 'multi', 'date'].includes(column.type);
          const parsed = immediate ? (value ?? null) : parseCell(column, draft);

          onAdvance(parsed);
          skipBlur.current = !immediate;
          setEditing(false);
          setDraft(cellText(parsed));
          setError('');
        } catch (error) {
          setError((error as Error).message);
        }
      }}
    >
      <div className="table-cell-value">{field}</div>
      <IconButton
        size="compact"
        className="table-cell-copy"
        label={`Copy ${column.name}, row ${rowNumber}`}
        title={copied ? 'Copied' : `Copy ${column.name}`}
        onPointerDown={event => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onMouseDown={event => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onClick={event => {
          event.stopPropagation();
          void copy();
        }}
      >
        {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
      </IconButton>
      <span className="sr-only" role="status">
        {copied ? `${column.name} copied` : ''}
      </span>
      {(error || copyError) && (
        <span role="alert" className="cell-error">
          {error || copyError}
        </span>
      )}
    </div>
  );
}

function TypedTableView({ node, updateAttributes: mutate, selected, editor }: NodeViewProps) {
  const locked = useEditorState({ editor, selector: ({ editor }) => !editor.isEditable });

  const updateAttributes: typeof mutate = patch => {
    if (editor.isEditable) {
      mutate(patch);
    }
  };

  const table = (node.attrs.table ?? newTable()) as TypedTable;
  const [deletingColumn, setDeletingColumn] = useState<string | null>(null);

  const [dragging, setDragging] = useState<string | null>(null),
    [dropAt, setDropAt] = useState<number | null>(null),
    [orderMessage, setOrderMessage] = useState('');

  const drag = useRef<{ id: string; y: number; moved: boolean; to: number | null } | null>(null);

  const reorder = (id: string, to: number) => {
    const next = moveColumn(table, id, to);

    if (next !== table) {
      updateAttributes({ table: next });

      setOrderMessage(
        `${next.columns[to].name} moved to column ${to + 1} of ${next.columns.length}.`,
      );
    }
  };

  const cancelDrag = () => {
    drag.current = null;
    setDragging(null);
    setDropAt(null);
  };

  const trackDrag = (event: PointerEvent<HTMLButtonElement>) => {
    const current = drag.current;

    if (!current) {
      return;
    }

    event.stopPropagation();

    if (Math.abs(event.clientY - current.y) > 4) {
      current.moved = true;
    }

    const list = event.currentTarget.closest('.column-settings');

    const row = document
      .elementsFromPoint(event.clientX, event.clientY)
      .map(element => element.closest<HTMLElement>('[data-column-id]'))
      .find(element => element && list?.contains(element));

    current.to = row ? table.columns.findIndex(column => column.id === row.dataset.columnId) : null;
    setDropAt(current.to);
    const panel = event.currentTarget.closest('.ui-control-popover');

    if (panel) {
      const rect = panel.getBoundingClientRect();

      if (event.clientY < rect.top + 40) {
        panel.scrollTop -= 16;
      } else if (event.clientY > rect.bottom - 40) {
        panel.scrollTop += 16;
      }
    }
  };

  const pendingColumn = table.columns.find(column => column.id === deletingColumn);

  const setTable = (patch: Partial<TypedTable>) =>
    updateAttributes({ table: { ...table, ...patch } });

  const [draftRowId, setDraftRowId] = useState<string | null>(null),
    [rowMessage, setRowMessage] = useState('');

  const tableElement = useRef<HTMLDivElement>(null),
    focusRow = useRef<string | null>(null);

  const filteredRows = tableView(table),
    draftRow = table.rows.find(row => row.id === draftRowId);

  const rows = draftRow
    ? [...filteredRows.filter(row => row.id !== draftRowId), draftRow]
    : filteredRows;

  const addRow = (edit?: { rowId: string; columnId: string; value: Cell }) => {
    if (!editor.isEditable) {
      return;
    }

    const next = appendTableRow(table, edit),
      id = next.rows[next.rows.length - 1].id;

    focusRow.current = id;
    setDraftRowId(id);
    setRowMessage('New row added.');
    updateAttributes({ table: next });
  };

  useEffect(() => {
    if (!focusRow.current) {
      return;
    }

    const frame = requestAnimationFrame(() => {
      const field = tableElement.current?.querySelector<HTMLElement>(
        `tr[data-row-id="${focusRow.current}"] td:first-child .table-cell-value :is(input,button[role="combobox"],button.ui-cell-choice)`,
      );

      if (field) {
        focusRow.current = null;
        field.focus();
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [draftRowId, table.rows]);

  const filterCount = (table.filters?.length ?? 0) + (table.filterColumn ? 1 : 0),
    sortCount = (table.sorts?.length ?? 0) + (table.sortColumn ? 1 : 0);

  const addColumn = () =>
    setTable({
      columns: [
        ...table.columns,
        { id: crypto.randomUUID(), name: 'New column', type: 'text', options: [] },
      ],
    });

  const updateColumn = (id: string, patch: Partial<Column>) =>
    setTable({ columns: table.columns.map(c => (c.id === id ? { ...c, ...patch } : c)) });

  return (
    <NodeViewWrapper
      className={`typed-table ${selected ? 'is-selected' : ''}`}
      contentEditable={false}
    >
      <div className="table-controls">
        <span className="table-caption">
          <Table2 aria-hidden="true" size={15} />
          Table{' '}
          <span>
            {rows.length}
            {filterCount ? ` / ${table.rows.length}` : ''}{' '}
            {table.rows.length === 1 ? 'row' : 'rows'}
          </span>
        </span>
        {!locked && (
          <div className="table-tools">
            <ControlPopover
              label="Table filters"
              trigger={
                <IconButton
                  size="compact"
                  label="Filter table"
                  className={filterCount ? 'active' : ''}
                  title={filterCount ? `Filter table · ${filterCount} active` : 'Filter table'}
                >
                  <Filter aria-hidden="true" />
                </IconButton>
              }
            >
              <strong>Filter</strong>
              <div className="table-tool-fields">
                <label>
                  Property
                  <Select
                    aria-label="Filter column"
                    value={table.filterColumn}
                    onChange={e => setTable({ filterColumn: e.target.value })}
                  >
                    <option value="">All rows</option>
                    {table.columns.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </Select>
                </label>
                {table.filterColumn && (
                  <>
                    <Select
                      aria-label="Filter operator"
                      value={table.filterOperator}
                      onChange={e =>
                        setTable({ filterOperator: e.target.value as TypedTable['filterOperator'] })
                      }
                    >
                      <option value="contains">contains</option>
                      <option value="equals">equals</option>
                      <option value="gt">greater / after</option>
                      <option value="lt">less / before</option>
                      <option value="empty">is empty</option>
                    </Select>
                    {table.filterOperator !== 'empty' && (
                      <Input
                        aria-label="Filter value"
                        placeholder="Value…"
                        value={table.filterValue}
                        onChange={e => setTable({ filterValue: e.target.value })}
                      />
                    )}
                    <div className="button-row">
                      <Button
                        size="compact"
                        onClick={() => setTable({ filterColumn: '', filterValue: '' })}
                      >
                        Clear
                      </Button>
                      <ActionIconButton
                        action="filter"
                        label="Keep condition & add another"
                        size="compact"
                        onClick={() =>
                          setTable({
                            filters: [
                              ...(table.filters ?? []),
                              {
                                column: table.filterColumn,
                                operator: table.filterOperator,
                                value: table.filterValue,
                              },
                            ],
                            filterColumn: '',
                            filterValue: '',
                          })
                        }
                      />
                    </div>
                  </>
                )}
              </div>
              {(table.filters ?? []).map((filter, index) => (
                <div className="table-rule" key={index}>
                  <span>
                    AND {table.columns.find(c => c.id === filter.column)?.name} {filter.operator}{' '}
                    {filter.value}
                  </span>
                  <IconButton
                    size="compact"
                    label={`Remove filter ${index + 1}`}
                    onClick={() =>
                      setTable({ filters: table.filters?.filter((_, i) => i !== index) })
                    }
                  >
                    <X aria-hidden="true" />
                  </IconButton>
                </div>
              ))}
            </ControlPopover>
            <ControlPopover
              label="Table sorting"
              trigger={
                <IconButton
                  size="compact"
                  label="Sort table"
                  className={sortCount ? 'active' : ''}
                  title={sortCount ? `Sort table · ${sortCount} active` : 'Sort table'}
                >
                  <ArrowUpDown aria-hidden="true" />
                </IconButton>
              }
            >
              <strong>Sort</strong>
              <div className="table-tool-fields">
                <label>
                  Property
                  <Select
                    aria-label="Sort column"
                    value={table.sortColumn}
                    onChange={e => setTable({ sortColumn: e.target.value })}
                  >
                    <option value="">Original order</option>
                    {table.columns.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </Select>
                </label>
                {table.sortColumn && (
                  <div className="button-row">
                    <IconButton
                      size="compact"
                      label="Reverse sort"
                      title={
                        table.sortDirection === 'asc'
                          ? 'Ascending; click for descending'
                          : 'Descending; click for ascending'
                      }
                      onClick={() =>
                        setTable({ sortDirection: table.sortDirection === 'asc' ? 'desc' : 'asc' })
                      }
                    >
                      {table.sortDirection === 'asc' ? (
                        <ArrowUp aria-hidden="true" />
                      ) : (
                        <ArrowDown aria-hidden="true" />
                      )}
                    </IconButton>
                    <ActionIconButton
                      action="sort"
                      label="Keep sort & add another"
                      size="compact"
                      onClick={() =>
                        setTable({
                          sorts: [
                            ...(table.sorts ?? []),
                            { column: table.sortColumn, direction: table.sortDirection },
                          ],
                          sortColumn: '',
                        })
                      }
                    />
                  </div>
                )}
              </div>
              {(table.sorts ?? []).map((sort, index) => (
                <div className="table-rule" key={index}>
                  <span>
                    {index + 1}. {table.columns.find(c => c.id === sort.column)?.name} ·{' '}
                    {sort.direction}
                  </span>
                  <IconButton
                    size="compact"
                    label={`Remove sort ${index + 1}`}
                    onClick={() => setTable({ sorts: table.sorts?.filter((_, i) => i !== index) })}
                  >
                    <X aria-hidden="true" />
                  </IconButton>
                </div>
              ))}
            </ControlPopover>
            <ControlPopover
              label="Table columns"
              trigger={
                <IconButton size="compact" label="Edit table columns">
                  <Settings2 aria-hidden="true" />
                </IconButton>
              }
            >
              <strong>Properties</strong>
              <div className="column-settings">
                <p className="column-order-help">Drag the dotted handles to reorder columns.</p>
                <span className="sr-only" role="status">
                  {orderMessage}
                </span>
                {table.columns.map((column, index) => (
                  <div
                    className={`column-setting-row ${dragging === column.id ? 'is-dragging' : ''} ${dragging && dropAt === index && dragging !== column.id ? (table.columns.findIndex(c => c.id === dragging) < index ? 'drop-after' : 'drop-before') : ''}`}
                    data-column-id={column.id}
                    key={column.id}
                  >
                    <IconButton
                      size="compact"
                      className="column-drag-handle"
                      label={`Reorder column ${column.name}`}
                      title="Drag to reorder. Keyboard: Arrow Up or Down; Home or End."
                      disabled={table.columns.length < 2}
                      onPointerDown={e => {
                        if (e.button !== 0) {
                          return;
                        }

                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.focus();
                        e.currentTarget.setPointerCapture(e.pointerId);
                        drag.current = { id: column.id, y: e.clientY, moved: false, to: null };
                        setDragging(column.id);
                      }}
                      onPointerMove={trackDrag}
                      onPointerUp={e => {
                        const current = drag.current;

                        if (!current) {
                          return;
                        }

                        e.stopPropagation();

                        if (current.moved && current.to !== null) {
                          reorder(current.id, current.to);
                        }

                        cancelDrag();

                        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                          e.currentTarget.releasePointerCapture(e.pointerId);
                        }
                      }}
                      onPointerCancel={cancelDrag}
                      onLostPointerCapture={cancelDrag}
                      onKeyDown={e => {
                        if (e.key === 'Escape' && drag.current) {
                          e.preventDefault();
                          e.stopPropagation();
                          cancelDrag();
                        } else if (['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) {
                          e.preventDefault();
                          e.stopPropagation();

                          reorder(
                            column.id,
                            e.key === 'Home'
                              ? 0
                              : e.key === 'End'
                                ? table.columns.length - 1
                                : index + (e.key === 'ArrowUp' ? -1 : 1),
                          );
                        }
                      }}
                    >
                      <GripVertical aria-hidden="true" />
                    </IconButton>
                    <Disclosure
                      className="column-setting"
                      summary={
                        <>
                          {column.name}
                          <span>{types.find(t => t.value === column.type)?.label}</span>
                        </>
                      }
                    >
                      <div className="column-setting-fields">
                        <Input
                          aria-label="Column name"
                          value={column.name}
                          onChange={e => updateColumn(column.id, { name: e.target.value })}
                        />
                        <Select
                          aria-label={`Type of ${column.name}`}
                          value={column.type}
                          onChange={e => {
                            const type = e.target.value as ColumnType;

                            const options = ['select', 'multi'].includes(type)
                              ? ['To do', 'In progress', 'Done'].map(label => ({
                                  id: crypto.randomUUID(),
                                  label,
                                }))
                              : [];

                            updateAttributes({
                              table: changeEmptyColumnType(table, column.id, type, options),
                            });
                          }}
                          disabled={table.rows.some(row => cellText(row.cells[column.id]) !== '')}
                        >
                          {types.map(t => (
                            <option value={t.value} key={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </Select>
                        <ActionIconButton
                          action="trash"
                          label={`Delete column ${column.name}`}
                          size="compact"
                          type="button"
                          disabled={table.columns.length === 1}
                          title={
                            table.columns.length === 1
                              ? 'Keep at least one column; remove the table block to delete the whole table.'
                              : undefined
                          }
                          onClick={() => setDeletingColumn(column.id)}
                        />
                        {['select', 'multi'].includes(column.type) && (
                          <div className="option-settings">
                            {optionsFor(column).map(option => (
                              <div className="button-row" key={option.id}>
                                <Input
                                  aria-label={`Option label in ${column.name}`}
                                  value={option.label}
                                  onChange={e =>
                                    updateColumn(column.id, {
                                      options: optionsFor(column).map(o =>
                                        o.id === option.id ? { ...o, label: e.target.value } : o,
                                      ),
                                    })
                                  }
                                />
                                <IconButton
                                  variant="danger"
                                  size="compact"
                                  label={`Remove option ${option.label}`}
                                  disabled={table.rows.some(
                                    r =>
                                      r.cells[column.id] === option.id ||
                                      (Array.isArray(r.cells[column.id]) &&
                                        (r.cells[column.id] as string[]).includes(option.id)),
                                  )}
                                  onClick={() =>
                                    updateColumn(column.id, {
                                      options: optionsFor(column).filter(o => o.id !== option.id),
                                    })
                                  }
                                >
                                  <Trash2 size={12} />
                                </IconButton>
                              </div>
                            ))}
                            <ActionIconButton
                              action="add"
                              label="Add option"
                              size="compact"
                              onClick={() =>
                                updateColumn(column.id, {
                                  options: [
                                    ...optionsFor(column),
                                    { id: crypto.randomUUID(), label: 'New option' },
                                  ],
                                })
                              }
                            />
                          </div>
                        )}
                      </div>
                    </Disclosure>
                  </div>
                ))}
                <ActionIconButton
                  action="column"
                  label="Add column"
                  size="compact"
                  onClick={() =>
                    setTable({
                      columns: [
                        ...table.columns,
                        { id: crypto.randomUUID(), name: 'New column', type: 'text', options: [] },
                      ],
                    })
                  }
                />
                <p>
                  Choose a column type before entering values. Existing values are never silently
                  converted. Keep at least one column, or remove the whole table block.
                </p>
              </div>
              {pendingColumn && (
                <div
                  className="column-delete-confirmation"
                  role="alert"
                  onKeyDown={e => {
                    if (e.key === 'Escape') {
                      e.stopPropagation();
                      setDeletingColumn(null);
                    }
                  }}
                >
                  <strong>Delete “{pendingColumn.name}”?</strong>
                  <p>
                    This removes the column and its values in all {table.rows.length} rows,
                    including hidden rows. Sorts and filters using this column will also be removed.
                  </p>
                  <div className="button-row wrap">
                    <Button
                      size="compact"
                      type="button"
                      autoFocus
                      onClick={() => setDeletingColumn(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="compact"
                      type="button"
                      variant="danger"
                      onClick={() => {
                        updateAttributes({ table: removeColumn(table, pendingColumn.id) });
                        setDeletingColumn(null);
                      }}
                    >
                      Delete column and values
                    </Button>
                  </div>
                </div>
              )}
            </ControlPopover>
          </div>
        )}
      </div>
      <div className="table-scroll" ref={tableElement}>
        <table aria-label="Note table">
          <thead>
            <tr>
              {table.columns.map(column => {
                const Icon = typeIcons[column.type];

                return (
                  <th key={column.id} scope="col">
                    <span className="table-column-heading">
                      <Icon aria-hidden="true" size={15} />
                      {column.name}
                    </span>
                  </th>
                );
              })}
              <th scope="col" className="table-row-actions">
                <ActionIconButton
                  disabled={locked}
                  action="add"
                  variant="ghost"
                  label="Add column"
                  size="compact"
                  onClick={addColumn}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={row.id} data-row-id={row.id}>
                {table.columns.map((column, columnIndex) => (
                  <td key={column.id}>
                    <CellEditor
                      locked={locked}
                      column={column}
                      value={row.cells[column.id]}
                      rowNumber={rowIndex + 1}
                      onAdvance={
                        rowIndex === rows.length - 1 && columnIndex === table.columns.length - 1
                          ? value => addRow({ rowId: row.id, columnId: column.id, value })
                          : undefined
                      }
                      onChange={value =>
                        setTable({
                          rows: table.rows.map(r =>
                            r.id === row.id
                              ? { ...r, cells: { ...r.cells, [column.id]: value } }
                              : r,
                          ),
                        })
                      }
                    />
                  </td>
                ))}
                <td className="table-row-actions">
                  <ControlPopover
                    size="compact"
                    label={`Actions for row ${rowIndex + 1}`}
                    trigger={
                      <IconButton
                        disabled={locked}
                        size="compact"
                        label={`Actions for row ${rowIndex + 1}`}
                      >
                        <Ellipsis aria-hidden="true" />
                      </IconButton>
                    }
                  >
                    <ActionIconButton
                      action="trash"
                      size="compact"
                      label="Remove row"
                      onClick={() => setTable({ rows: table.rows.filter(r => r.id !== row.id) })}
                    />
                  </ControlPopover>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <span className="sr-only" role="status">
        {rowMessage}
      </span>
      {draftRow && !filteredRows.some(row => row.id === draftRowId) && (
        <p className="table-empty">
          The new row stays visible while you fill it in. Your filters still apply to other rows.
        </p>
      )}
      {rows.length === 0 && (
        <p className="table-empty">
          {table.rows.length
            ? 'No rows match this filter. Your other rows are still here.'
            : 'No rows yet.'}
        </p>
      )}
      {!locked && (
        <div className="table-footer">
          <ActionIconButton
            action="add"
            variant="ghost"
            label="New row"
            size="compact"
            onClick={() => {
              try {
                addRow();
              } catch (error) {
                setRowMessage((error as Error).message);
              }
            }}
          />
        </div>
      )}
    </NodeViewWrapper>
  );
}

export const TypedTableExtension = Node.create({
  name: 'typedTable',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes() {
    return { table: { default: null } };
  },
  parseHTML() {
    return [{ tag: 'div[data-typed-table]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-typed-table': '' })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(TypedTableView, {
      stopEvent: ({ event }) => {
        // SVG descendants are not BUTTON targets. Stop native ProseMirror handlers before
        // React's delegated events so a copy tap cannot select the table atom.
        const target = event.target instanceof Element ? event.target : null;

        return (
          !event.type.startsWith('drag') &&
          event.type !== 'drop' &&
          Boolean(target?.closest('button,input,textarea,select,[role="combobox"]'))
        );
      },
    });
  },
});
